import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Stack,
  Text,
  useToast,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { RangeDatepicker } from 'chakra-dayzed-datepicker';
import { useState, useCallback, useMemo, useEffect } from 'react';
import { djs } from '@duke-hq/djs';
import { ArrowRightFromLine, ChevronDown } from 'lucide-react';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { useSubSidebar } from '@duke-hq/ui/components/SubSidebar/useSubSidebar';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { withPermission } from '../withPermission';

export const InvoicesXeroPage = withPermission('canViewFinances', () => {
  const { isExpanded, toggleExpandSubSidebar } = useSubSidebar();
  const toast = useToast();
  const defaultEndDate = new Date();
  const defaultStartDate = new Date(defaultEndDate);
  defaultStartDate.setDate(defaultStartDate.getDate() - 1);
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>({
    start: defaultStartDate,
    end: defaultEndDate,
  });

  const getInvoices = trpcAdmin.finances.invoiceXero.getByDate.useMutation({
    onSuccess: (res) => {
      if (!res.invoiceData) {
        toast({
          title: t`No data found`,
          description: t`No invoice data found for the selected date range.`,
          status: 'info',
          duration: 5000,
          isClosable: true,
          variant: 'left-accent',
        });
        return;
      }
    },
    onError: () => {
      toast({
        title: t`Error getting invoices`,
        description: t`An error occurred while getting the invoices. Please try again.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const syncByDate = trpcAdmin.finances.invoiceXero.syncByDate.useMutation({
    onSuccess: async (res) => {
      if (!res || res.length === 0) {
        toast({
          title: t`No data synced`,
          description: t`No invoice data were synced.`,
          status: 'info',
          duration: 5000,
          isClosable: true,
          variant: 'left-accent',
        });
        return;
      }

      await getInvoices.mutateAsync({
        fromDate: dateRange.start,
        toDate: dateRange.end,
      });
      setSelectedIds([]);
      toast({
        title: t`Published`,
        description: t`Selected invoices have been created on Xero and the table has been refreshed.`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    },
    onError: () => {
      toast({
        title: t`Error publishing the data`,
        description: t`An error occurred while publishing the data. Please try again.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const handleDownloadCSVClick = useCallback(() => {
    void (async () =>
      getInvoices.mutateAsync({
        fromDate: dateRange.start,
        toDate: dateRange.end,
      }))();
  }, [getInvoices, dateRange]);

  const setDateRangeFromNow = useCallback((days: number) => {
    const end = djs();
    const start = djs().subtract(days, 'days');
    setDateRange({ start: start.toDate(), end: end.toDate() });
  }, []);

  const setDateRangeFromStartAndEndOfDays = useCallback((range: Date[]) => {
    const start = djs(range[0]).startOf('day').toDate();
    const end = djs(range[1]).endOf('day').toDate();
    setDateRange({ start, end });
  }, []);

  const getRowId = useCallback((row: any): string | null => {
    const raw = row?.invoiceNumber;
    return raw == null ? null : String(raw);
  }, []);

  // Keep selection in sync with current dataset
  useEffect(() => {
    if (!getInvoices.data?.invoiceData) {
      setSelectedIds([]);
      return;
    }
    const currentIds = new Set(
      getInvoices.data.invoiceData
        .map((r: any) => getRowId(r))
        .filter((v: string | null): v is string => v !== null),
    );
    setSelectedIds((prev) => prev.filter((id) => currentIds.has(id)));
  }, [getInvoices.data?.invoiceData, getRowId]);

  // ===== Sorting =====
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(
    null,
  );

  const sortedData = useMemo(() => {
    if (!getInvoices.data?.invoiceData) {
      return [];
    }
    const data = [...getInvoices.data.invoiceData];
    if (sortConfig) {
      data.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return data;
  }, [getInvoices.data?.invoiceData, sortConfig]);

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  // ===== Bulk select helpers (based on invoiceNumber) =====
  const allIds = useMemo<string[]>(
    () =>
      Array.from(
        new Set(
          sortedData
            .map((r: any) => getRowId(r))
            .filter((v: string | null): v is string => v !== null),
        ),
      ),
    [sortedData, getRowId],
  );

  const isAllSelected = selectedIds.length > 0 && selectedIds.length === allIds.length;
  const isIndeterminate =
    selectedIds.length > 0 && selectedIds.length < allIds.length && allIds.length > 0;

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => (prev.length === allIds.length ? [] : allIds));
  }, [allIds]);

  // Per-row select toggle
  const handleSelect = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  // Publish selected invoiceNumbers
  const handleSyncSelected = useCallback(() => {
    void (async () =>
      syncByDate.mutateAsync({
        fromDate: dateRange.start,
        toDate: dateRange.end,
        enrolmentIds: selectedIds,
      }))();
  }, [syncByDate, dateRange, selectedIds]);

  return (
    <TableViewLayout
      pageHeader={[
        !isExpanded && (
          <IconButton
            aria-label="expand secondary sidebar"
            icon={<ArrowRightFromLine />}
            variant="tertiary"
            onClick={toggleExpandSubSidebar}
            size="md"
          />
        ),
        <PageHeading key="page-heading">
          <Trans>Invoices</Trans>
        </PageHeading>,
      ]}
      tableActionsRight={
        <VStack spacing={8} p={4} align="end">
          <HStack justifyContent="flex-end" alignItems="flex-end">
            <Box>
              <FormControl id="date-range" isRequired>
                <FormLabel htmlFor="date-range" mt="0">
                  <Trans>Date Range</Trans>
                </FormLabel>
                <ButtonGroup isAttached variant="outline" isDisabled={getInvoices.isPending}>
                  <RangeDatepicker
                    triggerVariant="input"
                    selectedDates={[dateRange.start, dateRange.end]}
                    onDateChange={setDateRangeFromStartAndEndOfDays}
                    maxDate={new Date()}
                    configs={{ dateFormat: 'dd-MM-yyyy' }}
                    propsConfigs={{
                      // A clicked month button that becomes disabled would take focus out of
                      // the popover and close it, so clicks leave focus where it is.
                      dateNavBtnProps: { onMouseDown: (event) => event.preventDefault() },
                    }}
                  />
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      aria-label={t`Date range select options`}
                      icon={<Icon as={ChevronDown} size="lg" />}
                      variant="unstyled"
                    />
                    <MenuList>
                      <MenuItem onClick={() => setDateRangeFromNow(1)}>
                        <Trans>Last 24 hours</Trans>
                      </MenuItem>
                      <MenuItem onClick={() => setDateRangeFromNow(3)}>
                        <Trans>Last 3 days</Trans>
                      </MenuItem>
                      <MenuItem onClick={() => setDateRangeFromNow(7)}>
                        <Trans>Last 7 days</Trans>
                      </MenuItem>
                      <MenuItem onClick={() => setDateRangeFromNow(30)}>
                        <Trans>Last 30 days</Trans>
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </ButtonGroup>
              </FormControl>
            </Box>
            <Button
              variant="primary"
              onClick={handleDownloadCSVClick}
              isLoading={getInvoices.isPending}
            >
              <Trans>Load invoices</Trans>
            </Button>
          </HStack>
        </VStack>
      }
      table={
        <Box
          bg="bg.surface"
          boxShadow={{ base: 'none', md: 'sm' }}
          borderRadius={{ base: 'none', md: 'lg' }}
        >
          <Stack spacing="5">
            <Box p={10}>
              {getInvoices.data?.invoiceData ? (
                <Box maxH="40vh" overflowY="auto" borderRadius="md">
                  <Table variant="simple" size="md" minWidth="900px">
                    {/* Sticky header */}
                    <Thead position="sticky" top={0} zIndex={1} bg="bg.surface">
                      <Tr>
                        <Th width="48px">
                          <Checkbox
                            isChecked={isAllSelected}
                            isIndeterminate={isIndeterminate}
                            onChange={toggleSelectAll}
                            aria-label="Select all"
                            isDisabled={sortedData.length === 0}
                            data-testid="select-all"
                          />
                        </Th>
                        <Th
                          onClick={() => handleSort('invoiceNumber')}
                          cursor="pointer"
                          width="180px"
                        >
                          Invoice Number
                          {sortConfig?.key === 'invoiceNumber'
                            ? sortConfig.direction === 'asc'
                              ? ' ▲'
                              : ' ▼'
                            : ''}
                        </Th>
                        <Th onClick={() => handleSort('reference')} cursor="pointer" width="160px">
                          Reference
                          {sortConfig?.key === 'reference'
                            ? sortConfig.direction === 'asc'
                              ? ' ▲'
                              : ' ▼'
                            : ''}
                        </Th>
                        <Th onClick={() => handleSort('contact')} cursor="pointer" width="200px">
                          Contact
                          {sortConfig?.key === 'contact'
                            ? sortConfig.direction === 'asc'
                              ? ' ▲'
                              : ' ▼'
                            : ''}
                        </Th>
                        <Th onClick={() => handleSort('date')} cursor="pointer" width="140px">
                          Invoice Date
                          {sortConfig?.key === 'date'
                            ? sortConfig.direction === 'asc'
                              ? ' ▲'
                              : ' ▼'
                            : ''}
                        </Th>
                        <Th onClick={() => handleSort('dueDate')} cursor="pointer" width="140px">
                          Invoice Due Date
                          {sortConfig?.key === 'dueDate'
                            ? sortConfig.direction === 'asc'
                              ? ' ▲'
                              : ' ▼'
                            : ''}
                        </Th>
                        <Th
                          onClick={() => handleSort('inventoryItemCode')}
                          cursor="pointer"
                          width="140px"
                        >
                          Inventory Item Code
                          {sortConfig?.key === 'inventoryItemCode'
                            ? sortConfig.direction === 'asc'
                              ? ' ▲'
                              : ' ▼'
                            : ''}
                        </Th>
                        <Th onClick={() => handleSort('status')} cursor="pointer" width="140px">
                          Status
                          {sortConfig?.key === 'status'
                            ? sortConfig.direction === 'asc'
                              ? ' ▲'
                              : ' ▼'
                            : ''}
                        </Th>
                        <Th onClick={() => handleSort('unitAmount')} cursor="pointer" width="140px">
                          Unit Amount
                          {sortConfig?.key === 'unitAmount'
                            ? sortConfig.direction === 'asc'
                              ? ' ▲'
                              : ' ▼'
                            : ''}
                        </Th>
                        <Th
                          onClick={() => handleSort('accountCode')}
                          cursor="pointer"
                          width="140px"
                        >
                          Account Code
                          {sortConfig?.key === 'accountCode'
                            ? sortConfig.direction === 'asc'
                              ? ' ▲'
                              : ' ▼'
                            : ''}
                        </Th>
                        <Th
                          onClick={() => handleSort('description')}
                          cursor="pointer"
                          width="140px"
                        >
                          Description
                          {sortConfig?.key === 'description'
                            ? sortConfig.direction === 'asc'
                              ? ' ▲'
                              : ' ▼'
                            : ''}
                        </Th>
                        <Th
                          onClick={() => handleSort('employeeGroup')}
                          cursor="pointer"
                          width="140px"
                        >
                          Employee Group
                          {sortConfig?.key === 'employeeGroup'
                            ? sortConfig.direction === 'asc'
                              ? ' ▲'
                              : ' ▼'
                            : ''}
                        </Th>
                        <Th onClick={() => handleSort('contract')} cursor="pointer" width="140px">
                          Contract
                          {sortConfig?.key === 'contract'
                            ? sortConfig.direction === 'asc'
                              ? ' ▲'
                              : ' ▼'
                            : ''}
                        </Th>
                      </Tr>
                    </Thead>

                    <Tbody>
                      {sortedData.map((row) => {
                        const rid = getRowId(row);
                        return (
                          <Tr key={rid ?? JSON.stringify(row)}>
                            <Td width="48px">
                              <Checkbox
                                isChecked={!!rid && selectedIds.includes(rid)}
                                isDisabled={!rid}
                                onChange={() => rid && handleSelect(rid)}
                                aria-label={`Select ${rid ?? 'row'}`}
                                data-testid={`select-${rid ?? 'row'}`}
                              />
                            </Td>
                            <Td width="180px" whiteSpace="nowrap">
                              {String(row.invoiceNumber ?? '')}
                            </Td>
                            <Td width="160px" whiteSpace="nowrap">
                              {String(row.reference ?? '')}
                            </Td>
                            <Td width="200px" whiteSpace="nowrap">
                              {row.contact?.name ?? ''}
                            </Td>
                            <Td width="140px" whiteSpace="nowrap">
                              {String(row.date ?? '')}
                            </Td>
                            <Td width="140px" whiteSpace="nowrap">
                              {String(row.dueDate ?? '')}
                            </Td>
                            <Td width="140px" whiteSpace="nowrap">
                              {String(row.lineItems?.[0]?.itemCode ?? '')}
                            </Td>
                            <Td width="140px" whiteSpace="nowrap">
                              {typeof row.status === 'undefined'
                                ? 'not synced'
                                : String(row.status)}
                            </Td>
                            <Td width="140px" whiteSpace="nowrap">
                              {String(row.lineItems?.[0]?.unitAmount ?? '')}
                            </Td>
                            <Td width="140px" whiteSpace="nowrap">
                              {String(row.lineItems?.[0]?.accountCode ?? '')}
                            </Td>
                            <Td width="140px" whiteSpace="nowrap">
                              {String(row.lineItems?.[0]?.description ?? '')}
                            </Td>
                            <Td width="140px" whiteSpace="nowrap">
                              {String(row.lineItems?.[0]?.tracking?.[0]?.option ?? '')}
                            </Td>
                            <Td width="140px" whiteSpace="nowrap">
                              {String(row.lineItems?.[0]?.tracking?.[1]?.option ?? '')}
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>
              ) : null}
            </Box>

            {/* Publish selected button (unchanged) */}
            <Box p={6} textAlign="right">
              <HStack justify="space-between" mb={3}>
                <Text fontSize="sm" color="gray.600">
                  {selectedIds.length > 0
                    ? `${selectedIds.length} selected`
                    : sortedData.length
                      ? 'No rows selected'
                      : ''}
                </Text>
              </HStack>
              <Button
                variant="primary"
                onClick={handleSyncSelected}
                isDisabled={selectedIds.length === 0}
                isLoading={syncByDate.isPending}
              >
                <Trans>Sync Selected</Trans>
              </Button>
            </Box>
          </Stack>
        </Box>
      }
    />
  );
});
